import type { AmbientSignal } from './ambientTypes'

/**
 * In-memory ambient signal bus. Zero-cost when no subscribers exist and
 * bounded by a 5-minute ring buffer so it never leaks. Publishers include
 * AiJobContext, AiChatContext, FocusHorizonContext, checklistMutationService,
 * and the workspace pulse feed — every publisher pushes the same
 * `AmbientSignal` shape, so evaluators never care about the origin.
 */

const RING_WINDOW_MS = 5 * 60 * 1000
const MAX_BUFFER = 128

type Listener = (signal: AmbientSignal) => void

class AmbientSignalBus {
  private buffer: AmbientSignal[] = []
  private listeners: Set<Listener> = new Set()

  publish(signal: AmbientSignal) {
    this.prune(signal.timestamp)
    this.buffer.push(signal)
    if (this.buffer.length > MAX_BUFFER) this.buffer.splice(0, this.buffer.length - MAX_BUFFER)
    this.listeners.forEach((listener) => {
      try {
        listener(signal)
      } catch (error) {
        console.warn('[ambientBus] listener threw', error)
      }
    })
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  recent(now: number = Date.now()): AmbientSignal[] {
    this.prune(now)
    return [...this.buffer]
  }

  clear() {
    this.buffer = []
  }

  private prune(now: number) {
    const cutoff = now - RING_WINDOW_MS
    while (this.buffer.length > 0 && (this.buffer[0]?.timestamp ?? 0) < cutoff) {
      this.buffer.shift()
    }
  }
}

export const ambientSignalBus = new AmbientSignalBus()

/** Convenience helpers used by publisher call sites. */
export const publishAmbientSignal = (signal: Omit<AmbientSignal, 'timestamp'> & { timestamp?: number }) => {
  ambientSignalBus.publish({ ...signal, timestamp: signal.timestamp ?? Date.now() })
}
